import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Droplets, Loader2, Mail, MapPin, Phone, ShieldAlert,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  HIGH_PRIORITY: "bg-amber-100 text-amber-900 border-amber-300",
  RECOMMENDED: "bg-cyan-50 text-cyan-900 border-cyan-200",
  MONITOR_MAINTAIN: "bg-slate-100 text-slate-600 border-slate-200",
};

const scoreTone = (s) =>
  s == null ? "text-slate-400" : s >= 70 ? "text-teal-700" : s >= 50 ? "text-cyan-700" : "text-amber-700";

export default function HealthCheckActionPlan() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [picked, setPicked] = useState([]);
  const [contactMe, setContactMe] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/public/health-check-action/${token}`);
      setData(d);
      const cta = params.get("cta");
      if (cta) {
        setPicked((p) => (p.includes(cta) ? p : [...p, cta]));
        if (cta === "contact_me") setContactMe(true);
        api.post(`/public/health-check-action/${token}/click`, { cta_action: cta })
          .catch(() => {});
      }
    } catch (e) {
      setErr(e?.response?.status === 404
        ? "This link is no longer available. Please contact our team and we'll send a new one."
        : "We couldn't load your action plan just now. Please try again shortly.");
    }
  }, [token, params]);
  useEffect(() => { load(); }, [load]);

  const toggle = (action) => {
    setPicked((p) => (p.includes(action) ? p.filter((x) => x !== action) : [...p, action]));
    api.post(`/public/health-check-action/${token}/click`, { cta_action: action })
      .catch(() => {});
  };

  const options = useMemo(() => {
    const rows = (data?.recommendations || []).map((r) => ({
      action: r.cta_action, label: r.section_label, title: r.title,
    }));
    const seen = new Set();
    return rows.filter((r) => (seen.has(r.action) ? false : seen.add(r.action)));
  }, [data]);

  const submit = async (opts = {}) => {
    const selections = opts.everything ? ["everything_recommended"] : picked;
    const wantsContact = opts.contactOnly ? true : contactMe;
    if (!selections.length && !wantsContact) {
      toast.error("Please choose at least one option");
      return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/health-check-action/${token}/submit`, {
        selections, contact_me: wantsContact, note,
      });
      setDone(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="mx-auto max-w-lg p-10 text-center" data-testid="hc-action-error">
      <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
      <p className="mt-3 text-base font-semibold text-slate-700">{err}</p>
    </div>
  );
  if (!data) return (
    <div className="flex justify-center p-16" data-testid="hc-action-loading">
      <Loader2 className="h-6 w-6 animate-spin text-cyan-700" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="hc-action-page">
      {/* header */}
      <div className="bg-[#0B2545] px-5 pb-10 pt-8 text-white">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-300">
            AWARE Water Testing · Tank Water Health Check
          </div>
          <h1 className="mt-3 text-3xl font-extrabold leading-tight sm:text-4xl">
            Your Water System Action Plan
          </h1>
          <div className="mt-4 flex items-start gap-2 text-sm text-slate-200">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span data-testid="hc-action-address">{data.property_address}</span>
          </div>
          <div className="mt-1 text-sm text-slate-300" data-testid="hc-action-date">
            Health Check {data.inspection_date}
            {data.technician ? ` · ${data.technician}` : ""} · {data.reference}
          </div>
          <div className="mt-6 flex flex-wrap items-end gap-6 rounded-2xl bg-white/5 p-5">
            <div>
              <div className={`text-4xl font-extrabold ${scoreTone(data.condition_score)} text-white`}
                data-testid="hc-action-score">
                {data.condition_score ?? "—"}<span className="text-lg font-bold text-slate-300"> / 100</span>
              </div>
              <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-cyan-200">
                Visible system condition score
              </div>
              <div className="text-sm font-semibold text-white">{data.condition_band}</div>
            </div>
            <p className="max-w-md text-xs leading-relaxed text-slate-300">
              This score reflects visible onsite observations of your rainwater collection,
              storage and treatment system. It is not a laboratory measure of water quality.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5">
        {done ? (
          <div className="-mt-6 rounded-2xl border border-teal-200 bg-white p-6 shadow-sm"
            data-testid="hc-action-submitted">
            <div className="flex items-center gap-2 text-teal-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-extrabold uppercase tracking-wide">Request received</span>
            </div>
            <h2 className="mt-3 text-xl font-extrabold text-slate-900">What happens next?</h2>
            <ol className="mt-3 space-y-2 text-sm text-slate-600">
              {(done.what_happens_next || []).map((s, i) => (
                <li key={s} className="flex gap-3">
                  <span className="font-extrabold text-cyan-700">{i + 1}</span>{s}
                </li>
              ))}
            </ol>
            {done.book_water_test_url && (
              <a href={done.book_water_test_url} data-testid="hc-action-book-test"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-cyan-700 px-6 py-3 text-sm font-extrabold text-white">
                CONTINUE TO BOOK MY WATER TEST <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
        ) : null}

        {/* assessment */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="hc-action-assessment">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-cyan-700">
            Technician assessment
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            {data.customer_facing_assessment}
          </p>
        </div>

        {/* findings */}
        {!!(data.key_findings || []).length && (
          <div className="mt-6" data-testid="hc-action-findings">
            <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-cyan-700">
              Key findings
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {data.key_findings.map((f) => (
                <div key={f.code} data-testid={`hc-action-finding-${f.code}`}
                  className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start gap-2">
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${f.severity === "high" ? "bg-amber-500" : "bg-cyan-600"}`} />
                    <div>
                      <div className="text-sm font-bold text-slate-900">{f.label}</div>
                      <div className="mt-1 text-xs leading-relaxed text-slate-500">{f.detail}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* recommendations */}
        <div className="mt-8" data-testid="hc-action-recommendations">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-cyan-700">
            Your recommended action plan
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            We have arranged these in the order we suggest considering them — visible system
            issues first, then treatment, then confirming water quality.
          </p>
          <div className="mt-4 space-y-4">
            {(data.recommendations || []).map((r, i) => (
              <div key={r.recommendation_type} data-testid={`hc-action-rec-${r.recommendation_type}`}
                className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                    Priority {i + 1}
                  </span>
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.RECOMMENDED}`}>
                    {r.priority_label}
                  </span>
                </div>
                <h3 className="mt-2 text-lg font-extrabold text-slate-900">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.reason}</p>
                <p className="mt-3 text-sm font-bold text-slate-900">
                  Recommended action: <span className="font-semibold text-slate-700">{r.scope}</span>
                </p>
                <button type="button" data-testid={`hc-action-cta-${r.cta_action}`}
                  onClick={() => toggle(r.cta_action)}
                  className={`mt-4 w-full rounded-full px-5 py-3 text-xs font-extrabold tracking-wide transition-colors sm:w-auto ${picked.includes(r.cta_action)
                    ? "bg-teal-600 text-white" : "bg-cyan-700 text-white hover:bg-cyan-800"}`}>
                  {picked.includes(r.cta_action) ? `SELECTED · ${r.cta_label}` : r.cta_label}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* master CTA */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="hc-action-plan-box">
          <h2 className="text-lg font-extrabold text-slate-900">Want help with your next steps?</h2>
          <p className="mt-2 text-sm text-slate-600">
            We can help organise the recommended services, treatment options and laboratory
            testing for this property.
          </p>
          <div className="mt-4 space-y-2">
            {options.map((o) => (
              <label key={o.action} data-testid={`hc-action-option-${o.action}`}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" className="h-4 w-4" checked={picked.includes(o.action)}
                  onChange={() => toggle(o.action)} />
                {o.label}
              </label>
            ))}
            <label data-testid="hc-action-option-contact"
              className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
              <input type="checkbox" className="h-4 w-4" checked={contactMe}
                onChange={(e) => setContactMe(e.target.checked)} />
              I&apos;d like someone to contact me first
            </label>
          </div>
          <textarea data-testid="hc-action-note" rows={3} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything you'd like us to know (optional)"
            className="mt-4 w-full rounded-xl border border-slate-200 p-3 text-sm" />
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" disabled={busy} data-testid="hc-action-submit-plan"
              onClick={() => submit()}
              className="rounded-full bg-[#0B2545] px-6 py-3 text-xs font-extrabold tracking-wide text-white disabled:opacity-50">
              {busy ? "SENDING…" : "REQUEST MY WATER SYSTEM PLAN"}
            </button>
            <button type="button" disabled={busy} data-testid="hc-action-submit-everything"
              onClick={() => submit({ everything: true })}
              className="rounded-full border border-[#0B2545] px-6 py-3 text-xs font-extrabold tracking-wide text-[#0B2545] disabled:opacity-50">
              EVERYTHING RECOMMENDED
            </button>
            <button type="button" disabled={busy} data-testid="hc-action-submit-contact"
              onClick={() => submit({ contactOnly: true })}
              className="rounded-full border border-slate-300 px-6 py-3 text-xs font-extrabold tracking-wide text-slate-700 disabled:opacity-50">
              I&apos;D LIKE SOMEONE TO CONTACT ME
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            You don&apos;t need to decide everything today — you can come back to this page at
            any time and request help with the items that matter most to you.
          </p>
        </div>

        {/* contacts */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="hc-action-contacts">
          {[data.provider, data.laboratory].filter((x) => x?.name).map((cx) => (
            <div key={cx.name} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-extrabold text-slate-900">{cx.name}</div>
              {cx.purpose && <div className="mt-1 text-xs text-slate-500">{cx.purpose}</div>}
              {cx.phone && (
                <a href={`tel:${cx.phone.replace(/[^\d+]/g, "")}`}
                  className="mt-3 flex items-center gap-2 text-sm font-bold text-cyan-700">
                  <Phone className="h-4 w-4" />{cx.phone}
                </a>
              )}
              {cx.email && (
                <a href={`mailto:${cx.email}`}
                  className="mt-1 flex items-center gap-2 text-sm font-bold text-cyan-700">
                  <Mail className="h-4 w-4" />{cx.email}
                </a>
              )}
            </div>
          ))}
        </div>

        {data.report_url && (
          <a href={data.report_url} target="_blank" rel="noreferrer" data-testid="hc-action-report-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-cyan-700">
            <Droplets className="h-4 w-4" /> Open my full Health Check report (PDF)
          </a>
        )}

        <p className="mt-8 text-xs leading-relaxed text-slate-500" data-testid="hc-action-disclaimer">
          {data.disclaimer} It does not determine whether water meets microbiological,
          chemical or Australian Drinking Water Guideline values. Laboratory testing is
          required to measure water-quality parameters.
        </p>
        <div className="mt-8 border-t border-slate-200 pt-5">
          <p className="text-sm font-extrabold text-[#0B2545]">Better information. Clearer next steps.</p>
          <p className="text-xs font-bold text-slate-700">AWARE Water Testing</p>
          <p className="text-xs text-slate-500">Australian Water Analysis, Reporting &amp; Evaluation</p>
        </div>
      </div>
    </div>
  );
}
